'use client';

import { Skeleton } from '@/components/ui/skeleton';
import { PublicSummaryResponse } from '@/server/api/v1/dashboard/type';
import { api } from '@/trpc/client';
import { BarChart2, Building2, CheckCircle2, FileText } from 'lucide-react';
import { useTranslations } from 'next-intl';

type StatBoxProps = {
    icon: React.ReactNode;
    value: string | number;
    label: string;
    isLoading?: boolean;
};

const StatBox = ({ icon, value, label, isLoading }: StatBoxProps) => (
    <div className='relative overflow-hidden rounded-2xl bg-secondary/40 p-5 text-center ring-1 ring-secondary/50 backdrop-blur-sm transition-all hover:bg-secondary/70'>
        <div className='mb-3 flex justify-center'>
            <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/15 text-secondary-foreground'>
                {icon}
            </div>
        </div>
        {isLoading ? (
            <div className='flex flex-col items-center gap-2'>
                <Skeleton className='h-9 w-20 rounded-lg bg-secondary/20' />
                <Skeleton className='h-3 w-24 rounded bg-secondary/20' />
            </div>
        ) : (
            <>
                <p className='text-3xl font-bold tabular-nums text-secondary-foreground lg:text-4xl'>
                    {value}
                </p>
                <p className='mt-1 text-xs font-medium text-secondary-foreground'>
                    {label}
                </p>
            </>
        )}
    </div>
);

type Props = {
    summary: PublicSummaryResponse | null;
    isLoading: boolean;
};

const StatHeroBanner = ({ summary, isLoading }: Props) => {
    const transPage = useTranslations('PublicStatistics');
    const year = new Date().getFullYear();

    const { data: procedureData, isLoading: isLoadingProcedure } =
        api.procedure.GetValidProceduresNow_Page.useQuery({
            pageSize: 1,
        });

    const totalProcedures =
        procedureData?.data?.TotalCount?.toLocaleString('vi-VN') ?? '—';

    const totalDossier = summary
        ? summary.TotalDossierThisYear.toLocaleString('vi-VN')
        : '—';

    const onTimeRate = summary
        ? `${(summary.OnTimeRate * 100).toFixed(1).replace('.', ',')}%`
        : '—';

    const totalUnits = summary
        ? summary.TotalUnits.toLocaleString('vi-VN')
        : '—';

    return (
        <div className='relative overflow-hidden rounded-2xl bg-primary px-6 py-8 text-white shadow-lg'>
            {/* Decorative shapes */}
            <div className='pointer-events-none absolute -right-12 -top-12 h-56 w-56 rounded-full bg-white/[0.05]' />
            <div className='pointer-events-none absolute -bottom-16 right-32 h-44 w-44 rounded-full bg-white/[0.05]' />
            <div className='pointer-events-none absolute right-12 top-1/2 h-24 w-24 -translate-y-1/2 rounded-full bg-white/[0.04]' />
            <div className='pointer-events-none absolute bottom-4 left-1/3 h-16 w-16 rounded-full bg-white/[0.03]' />

            <div className='relative'>
                <div className='mb-1 flex items-center gap-2'>
                    <BarChart2 size={18} className='text-white/70' />
                    <span className='text-sm font-medium uppercase tracking-widest text-white/60'>
                        {year}
                    </span>
                </div>
                <h1 className='text-2xl font-bold tracking-tight sm:text-3xl'>
                    {transPage('PublicStatistics_Title')}
                </h1>
                <p className='mt-1.5 max-w-xl text-sm text-white/60'>
                    {transPage('PublicStatistics_Subtitle')}
                </p>

                <div className='mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4'>
                    <StatBox
                        icon={<FileText size={18} />}
                        value={totalProcedures}
                        label={transPage('PublicStatistics_TotalProcedures')}
                        isLoading={isLoadingProcedure}
                    />
                    <StatBox
                        icon={<BarChart2 size={18} />}
                        value={totalDossier}
                        label={transPage(
                            'PublicStatistics_TotalDossierThisYear'
                        )}
                        isLoading={isLoading}
                    />
                    <StatBox
                        icon={<CheckCircle2 size={18} />}
                        value={onTimeRate}
                        label={transPage('PublicStatistics_OnTimeRate')}
                        isLoading={isLoading}
                    />
                    <StatBox
                        icon={<Building2 size={18} />}
                        value={totalUnits}
                        label={transPage('PublicStatistics_TotalUnits')}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </div>
    );
};

export default StatHeroBanner;
